import React, { useRef, useState } from "react";
import { Alert, Modal, Radio } from "antd";
import { useTranslation } from "react-i18next";
import { usePutExternalGrantsMutation } from "../../../redux/externals/externalsAPI";
import GETJwtToken from "../../../redux/authentication/authentication";
import type {
  ExternalDetail,
  ExternalEndpointOption,
  ExternalMutationResult,
} from "../../../interfaces/External.interface";
import ApiClientEndpointChecklist from "./ApiClientEndpointChecklist";
import { grantAdditions, grantChanges, includesLiveRoute } from "./apiClientGrants";
import apiClientErrorMessage from "./apiClientErrors";

/**
 * How a save treats a client whose grants are not reported: "add" grants the
 * checked endpoints and revokes nothing; "replace" leaves exactly the checked
 * ones and revokes every other, including grants the page cannot see.
 */
type UnknownSaveMode = "add" | "replace";

/** What a save left the client holding. */
export interface SavedGrants {
  ids: string[];
  /** True when the client now holds exactly `ids`; false when it holds at least them. */
  exact: boolean;
}

interface ApiClientGrantsModalProps {
  /** The API client being edited; null closes the dialog. */
  client: ExternalDetail | null;
  /**
   * The grants LiftCloud reported for the client, or null when it reported
   * none (today, always).
   */
  current: string[] | null;
  /** What to check when the dialog opens: current, or what this page last saved. */
  initial: string[];
  endpoints: ExternalEndpointOption[];
  /** No catalog has arrived yet: the list is replaced by a spinner. */
  endpointsLoading: boolean;
  /** The catalog is being read again: Save waits for it. */
  endpointsRefreshing: boolean;
  /** Why the catalog cannot be used; Save is refused while it is set. */
  catalogProblem: string;
  onClose: () => void;
  /** Called with what the client now holds and the PUT's answer. */
  onSaved: (client: ExternalDetail, saved: SavedGrants, result: ExternalMutationResult) => void;
}

/** Edits which /api/ext endpoints an API client may call. */
function ApiClientGrantsModal({
  client,
  current,
  initial,
  endpoints,
  endpointsLoading,
  endpointsRefreshing,
  catalogProblem,
  onClose,
  onSaved,
}: ApiClientGrantsModalProps) {
  const [t] = useTranslation("global");
  // The page remounts this dialog per client (its `key`), so every opening
  // starts from `initial` and the add-only mode, never the last one's state.
  const [selected, setSelected] = useState<string[]>(initial);
  const [mode, setMode] = useState<UnknownSaveMode>("add");
  const [error, setError] = useState("");
  const [putExternalGrants] = usePutExternalGrantsMutation();
  // Set synchronously on the first submit and cleared when it settles, so a
  // second Enter or click during the token wait (before the mutation runs)
  // cannot send a second PUT. The state mirrors it for the buttons.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const unknown = current === null;
  const adding = unknown && mode === "add";
  const catalogUsable = !endpointsLoading && !endpointsRefreshing && catalogProblem === "";
  // A role that was deleted reads as a role_id with no role_name, and
  // LiftCloud treats that client as having none.
  const hasRole = Boolean(client?.role_id && client?.role_name);

  // The selection checked against the catalog just read: an id this page
  // remembered may name an entry deleted since, which LiftCloud answers with
  // a 500. Replace keeps only what the checklist shows (active entries); the
  // known-grants diff keeps inactive held ids, so it never revokes them.
  const catalogIds = new Set(endpoints.map((endpoint) => endpoint.id));
  const activeIds = new Set(
    endpoints.filter((endpoint) => endpoint.active).map((endpoint) => endpoint.id),
  );
  const held = new Set(initial);
  // Add-only sends only what is newly checked. Re-sending what the page
  // remembers would silently grant back an endpoint another administrator
  // revoked since; the page's record may be stale instead, as any is.
  const additions = selected.filter((id) => activeIds.has(id) && !held.has(id));
  // A reported grant missing from the catalog just read stays wanted, so it
  // is never sent to delete (LiftCloud answers an unknown id with a 500).
  const desired = unknown
    ? selected.filter((id) => activeIds.has(id))
    : selected.filter((id) => catalogIds.has(id) || held.has(id));

  const chooseMode = (next: UnknownSaveMode) => {
    setMode(next);
    // Back in add-only, what the client holds is shown checked again.
    if (next === "add") setSelected((previous) => Array.from(new Set([...previous, ...initial])));
  };

  const submit = async () => {
    if (!client || !catalogUsable || savingRef.current) return;
    if (adding && additions.length === 0) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    const changes = adding
      ? grantAdditions(additions)
      : grantChanges(current, desired, Array.from(catalogIds));
    try {
      const token = await GETJwtToken();
      const result = await putExternalGrants({
        token,
        id: client.id,
        name: client.name,
        ...changes,
      }).unwrap();
      onSaved(
        client,
        adding ? { ids: additions, exact: false } : { ids: desired, exact: true },
        result,
      );
    } catch (err) {
      setError(apiClientErrorMessage(err, t("apiClients.genericError")));
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={client !== null}
      title={t("apiClients.grantsTitle", { name: client?.name ?? "" })}
      okText={t("apiClients.saveGrants")}
      cancelText={t("apiClients.cancel")}
      onOk={submit}
      onCancel={saving ? undefined : onClose}
      closable={!saving}
      maskClosable={!saving}
      keyboard={!saving}
      confirmLoading={saving}
      okButtonProps={
        {
          "data-testid": "api-client-grants-submit",
          disabled: !catalogUsable || (adding && additions.length === 0),
        } as Record<string, unknown>
      }
      cancelButtonProps={
        { "data-testid": "api-client-grants-cancel", disabled: saving } as Record<string, unknown>
      }
      destroyOnClose
      width={720}
    >
      <div data-testid="api-client-grants-modal">
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className="mb-3"
            data-testid="api-client-grants-save-error"
          />
        )}
        {unknown && (
          <div className="mb-3" data-testid="api-client-grants-not-reported">
            <p className="mb-2">{t("apiClients.grantsNotReported")}</p>
            <Radio.Group value={mode} onChange={(event) => chooseMode(event.target.value)}>
              <Radio value="add" data-testid="api-client-grants-mode-add">
                {t("apiClients.grantsModeAdd")}
              </Radio>
              <Radio value="replace" data-testid="api-client-grants-mode-replace">
                {t("apiClients.grantsModeReplace")}
              </Radio>
            </Radio.Group>
            {mode === "replace" && (
              <Alert
                type="warning"
                showIcon
                message={t("apiClients.grantsReplaceWarning")}
                className="mt-2"
                data-testid="api-client-grants-replace-warning"
              />
            )}
          </div>
        )}
        {client && !hasRole && includesLiveRoute(selected, endpoints) && (
          <Alert
            type="warning"
            showIcon
            message={t("apiClients.liveNeedsRole")}
            className="mb-3"
            data-testid="api-client-grants-live-needs-role"
          />
        )}
        <ApiClientEndpointChecklist
          testId="api-client-grants"
          endpoints={endpoints}
          value={selected}
          onChange={setSelected}
          locked={adding ? initial : []}
          loading={endpointsLoading}
          error={catalogProblem}
          disabled={saving}
        />
      </div>
    </Modal>
  );
}

export default ApiClientGrantsModal;
